import {
  Badge,
  Button,
  Input,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuItemRadio,
  MenuList,
  MenuPopover,
  MenuTrigger,
  mergeClasses,
} from '@fluentui/react-components';
import {
  Add24Filled,
  Add24Regular,
  bundleIcon,
  ChevronDoubleDown20Regular,
  ChevronDoubleUp20Regular,
  ChevronDown20Regular,
  ChevronRight20Regular,
  Filter20Regular,
  Search20Regular,
  Sparkle20Regular,
} from '@fluentui/react-icons';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import {
  useGraderActions,
  useGraderState,
} from '../../../context/GraderContext';
import { getJudgeFactoryPath } from '../../../features/judge-factory';
import { useStyles as usePageStyles } from '../GraderPage.style';
import { getCreatableGraderTypes } from '../graderTypes/graderTypeRegistry.js';

import { useStyles } from './GraderList.style';
import { GraderListItem } from './GraderListItem';

// ---------------------------------------------------------------------------
// Bundled icons
// ---------------------------------------------------------------------------
const CreateIcon = bundleIcon(Add24Filled, Add24Regular) as React.FC;

function tagLabel(tag: string): string {
  return tag
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
//
// GraderList reads everything it needs from context:
//   * graders → already sorted newest-first by useGraders; rendered as-is.
//   * mode + selectedId → drives selection highlight + "ghost" entry.
//   * actions → selectGrader, enterCreateMode.
export function GraderList() {
  const styles = useStyles();
  const pageStyles = usePageStyles();

  const navigate = useNavigate();
  const { graders, mode, selectedId, workspaceName } = useGraderState();
  const { selectGrader, enterCreateMode } = useGraderActions();
  const [selectedTag, setSelectedTag] = useState('');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  useEffect(() => {
    setSelectedTag('');
    setSearch('');
    setCollapsed(new Set());
  }, [workspaceName]);

  const tags = [
    ...new Set(
      graders
        .map((judge) => judge.folder)
        .filter((tag): tag is string => Boolean(tag)),
    ),
  ].sort((a, b) => a.localeCompare(b));
  const untagged = graders.filter((judge) => !judge.folder);
  const groupKeys = [...tags, ...(untagged.length ? ['__untagged__'] : [])];
  const allCollapsed =
    groupKeys.length > 0 && groupKeys.every((key) => collapsed.has(key));
  const activeTag =
    tags.includes(selectedTag) ||
    (selectedTag === '__untagged__' && untagged.length > 0)
      ? selectedTag
      : '';
  const query = search.trim().toLowerCase();
  const filtered = graders.filter(
    (judge) =>
      (!activeTag ||
        (activeTag === '__untagged__'
          ? !judge.folder
          : judge.folder === activeTag)) &&
      (!query ||
        [
          judge.displayName,
          judge.name,
          judge.description,
          judge.folder,
          judge.folder ? tagLabel(judge.folder) : undefined,
        ].some((value) => value?.toLowerCase().includes(query))),
  );
  const groups = tags
    .filter((tag) => !activeTag || activeTag === tag)
    .map((tag) => ({
      key: tag,
      label: tagLabel(tag),
      judges: filtered.filter((judge) => judge.folder === tag),
    }))
    .filter((group) => group.judges.length > 0);
  const visibleUntagged = filtered.filter((judge) => !judge.folder);
  if (visibleUntagged.length > 0) {
    groups.push({
      key: '__untagged__',
      label: 'Untagged',
      judges: visibleUntagged,
    });
  }

  const toggleGroup = (key: string) => {
    setCollapsed((current) => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  return (
    <div className={pageStyles.leftPanel}>
      <div className={styles.listFilters}>
        <Input
          className={styles.searchControl}
          aria-label="Search judges"
          placeholder="Search judges"
          contentBefore={<Search20Regular />}
          value={search}
          onChange={(_, data) => setSearch(data.value)}
        />
        {tags.length > 0 && (
          <Menu
            checkedValues={{ tags: [activeTag] }}
            onCheckedValueChange={(_, data) =>
              setSelectedTag(data.checkedItems[0] ?? '')
            }
          >
            <MenuTrigger disableButtonEnhancement>
              <Button
                size="small"
                appearance={activeTag ? 'primary' : 'subtle'}
                aria-label={
                  activeTag
                    ? `Filter judges by tag: ${activeTag === '__untagged__' ? 'Untagged' : tagLabel(activeTag)}`
                    : 'Filter judges by tag'
                }
                icon={<Filter20Regular />}
              />
            </MenuTrigger>
            <MenuPopover className={styles.tagMenuPopover}>
              <MenuList>
                <MenuItemRadio name="tags" value="">
                  All tags
                </MenuItemRadio>
                {tags.map((tag) => (
                  <MenuItemRadio key={tag} name="tags" value={tag}>
                    {tagLabel(tag)}
                  </MenuItemRadio>
                ))}
                {untagged.length > 0 && (
                  <MenuItemRadio name="tags" value="__untagged__">
                    Untagged
                  </MenuItemRadio>
                )}
              </MenuList>
            </MenuPopover>
          </Menu>
        )}
        {tags.length > 0 && (
          <Button
            size="small"
            appearance="subtle"
            aria-label={
              allCollapsed ? 'Expand all groups' : 'Collapse all groups'
            }
            icon={
              allCollapsed ? (
                <ChevronDoubleDown20Regular />
              ) : (
                <ChevronDoubleUp20Regular />
              )
            }
            disabled={Boolean(query)}
            onClick={() =>
              setCollapsed(allCollapsed ? new Set() : new Set(groupKeys))
            }
          />
        )}
      </div>

      {/* Judge list */}
      <div className={pageStyles.judgeList}>
        {/* "Ghost" entry while the user is creating a new judge. Only
            rendered while the system-prompt create flow is active. */}
        {mode === 'create' && (
          <div
            className={mergeClasses(
              pageStyles.judgeListItem,
              pageStyles.judgeListItemNew,
            )}
          >
            <div className={pageStyles.judgeListItemHeader}>
              <span className={pageStyles.judgeListItemName}>New Judge</span>
              <Badge size="small" appearance="tint" color="brand">
                New
              </Badge>
            </div>
            <div className={pageStyles.judgeListItemMeta}>
              <span className={styles.creationStepLabel}>{'Editing…'}</span>
            </div>
          </div>
        )}

        {groups.map((group) => (
          <div key={group.key}>
            {tags.length > 0 && (
              <button
                type="button"
                className={styles.tagHeading}
                aria-label={`${group.label} group`}
                aria-expanded={Boolean(query) || !collapsed.has(group.key)}
                disabled={Boolean(query)}
                onClick={() => toggleGroup(group.key)}
              >
                <span>{group.label}</span>
                {Boolean(query) || !collapsed.has(group.key) ? (
                  <ChevronDown20Regular className={styles.tagChevron} />
                ) : (
                  <ChevronRight20Regular className={styles.tagChevron} />
                )}
              </button>
            )}
            {(query || !collapsed.has(group.key)) &&
              group.judges.map((judge) => (
                <GraderListItem
                  key={judge.id}
                  judge={judge}
                  isActive={mode === 'view' && judge.id === selectedId}
                  onSelect={selectGrader}
                />
              ))}
          </div>
        ))}
        {filtered.length === 0 && (
          <div className={styles.emptySearch}>No judges match your search.</div>
        )}
      </div>

      {/* Create menu — pick an authoring method, then enter its flow. Mirrors
          the Datasets page's CreateDatasetMenu. In-page grader types come from
          the registry, so new types appear here automatically; "Judge factory"
          is a special deep link to the generation page, not an in-page type. */}
      <div className={pageStyles.leftFooter}>
        <Menu>
          <MenuTrigger disableButtonEnhancement>
            <MenuButton
              appearance="primary"
              icon={<CreateIcon />}
              className={styles.fullWidthButton}
              disabled={mode === 'create'}
            >
              Create New Grader
            </MenuButton>
          </MenuTrigger>
          <MenuPopover>
            <MenuList className={styles.createMenuList}>
              <MenuItem
                icon={<Sparkle20Regular />}
                onClick={() =>
                  void navigate(getJudgeFactoryPath(workspaceName))
                }
              >
                <div className={styles.createMenuItemContent}>
                  <span className={styles.createMenuItemTitle}>
                    Judge factory
                  </span>
                  <span className={styles.createMenuItemDescription}>
                    Generate an optimized judge from a description.
                  </span>
                </div>
              </MenuItem>
              <MenuDivider />
              {getCreatableGraderTypes().map((option) => (
                <MenuItem
                  key={option.method}
                  icon={option.menuIcon}
                  onClick={() => enterCreateMode(option.method)}
                >
                  <div className={styles.createMenuItemContent}>
                    <span className={styles.createMenuItemTitle}>
                      {option.menuTitle}
                    </span>
                    <span className={styles.createMenuItemDescription}>
                      {option.menuDescription}
                    </span>
                  </div>
                </MenuItem>
              ))}
            </MenuList>
          </MenuPopover>
        </Menu>
      </div>
    </div>
  );
}
